// Wire-format serializers. Match @archlang/server's /api/model + /api/edges
// payloads exactly so the web client can consume loadViewer output through
// the same path it used the server through.

import {
  buildDescriptionIndex,
  buildBacklinkIndex,
  joinDescriptions,
  nodeDescriptionContext,
  makeDescriptionContext,
  renderDescription,
  renderMarkdownDocument,
  type Backlink,
  type DependencyGraph,
  type DependencyEdge,
  type DescriptionContext,
  type Diagnostic,
  type AspectEntry,
  type FieldDecl,
  type ResolvedSurface,
  type ResolvedInterface,
  moduleDiffId,
  processIdentity,
  type Name,
  type ResolvedModel,
  type ResolvedModule,
  type DecorationInstance,
  type Value,
} from "@archlang/engine";

/**
 * v0.9.0 §7: the planes a MEMBER is present on. Carried only when its node's
 * surface is plane-dependent (the node has a partial-presence block) — and then
 * EVERY member of that node is tagged, or an untagged one would draw on every
 * plane: a block declaration is on that plane alone, a block REFERENCE is on
 * both, and a main-body member is on the node's pre-block planes. Absent
 * entirely on a node that uses no partial presence.
 */
export type WirePlanePresent = { readonly planes?: readonly string[] };

export interface WireFieldEntry extends WirePlanePresent {
  readonly path: readonly string[];
  readonly value?: unknown;
  /** Getter provenance, when the value was resolved from `@x` / `@@x` — the
   *  LAST path segment is the chip key (`widget.label1: @ext.vendor` labels
   *  its chip "vendor"). Only the renderer-facing minimum crosses the wire. */
  readonly from?: { readonly kind: "getter"; readonly path: readonly string[] };
  /** True when the value is a type/kind template default, not author-written
   *  on this body. Renderers use it to tell a real pin (`widget.width: 300`)
   *  from an inherited default. */
  readonly fromTemplate?: boolean;
}

export interface WireAspectEntry {
  readonly path: readonly string[];
  readonly value?: unknown;
}

export interface WireInterface extends WirePlanePresent {
  readonly name: string;
  readonly typeName: string;
  /** @deprecated alias of `typeName`, kept for widgets that read `kindName`. */
  readonly kindName: string;
  readonly description?: string;
  readonly fields: readonly WireFieldEntry[];
  readonly aspects: readonly WireAspectEntry[];
  /** True for a stub interface synthesized from a process callee (v0.6,
   *  §4.13). Omitted for authored interfaces. */
  readonly synthesized?: boolean;
  /** True for the synthesized `(anonymous)` handler that stands in for an
   *  unspecified callee interface (§4.13.2). Rendered as "(unspecified)". */
  readonly anonymous?: boolean;
}

export interface WireSurface extends WirePlanePresent {
  readonly name: string;
  readonly typeName: string;
  /** @deprecated alias of `typeName`, kept for widgets that read `kindName`. */
  readonly kindName: string;
  readonly description?: string;
  readonly fields: readonly WireFieldEntry[];
  readonly aspects: readonly WireAspectEntry[];
  readonly surfaces: readonly WireSurface[];
  readonly interfaces: readonly WireInterface[];
}

export interface WireModule {
  readonly id?: string;
  readonly name: string;
  readonly typeName: string;
  /** @deprecated alias of `typeName`, kept for widgets that read `kindName`. */
  readonly kindName: string;
  readonly baseType: string;
  readonly description?: string;
  readonly fields: readonly WireFieldEntry[];
  readonly aspects: readonly WireAspectEntry[];
  readonly interfaces: readonly WireInterface[];
  readonly surfaces: readonly WireSurface[];
  readonly children: readonly WireModule[];
  /** `@name(args)` decorations written on this module, arg values flattened to
   *  text. `serializeModule` emits this; declaring it is what lets a consumer
   *  typed on the WIRE form (the diff handler, the MCP resolve tool, the server
   *  board route) read it without a cast — and what a future serializer edit
   *  breaks against. Structural mirror of scene's `DecorationRef`. */
  readonly decorations?: readonly {
    readonly name: string;
    readonly args: readonly { readonly name?: string; readonly value: string }[];
  }[];
  /**
   * POSIX path of the `.arch` file this module was declared in (e.g.
   * `/services/orders.arch`). Sourced from the decl's `SourceSpan.file`.
   * Undefined for modules whose decl carries no span (legacy/synthetic).
   */
  readonly sourceFile?: string;
  /**
   * Dotted name of the enclosing space (v0.5), e.g. `Corporate.Loans`.
   * Undefined for modules in the package root (no space) or single-space
   * packages. Derived from the model's file→space map via {@link sourceFile}.
   */
  readonly space?: string;
  /** v0.9.0 §1.1: the non-primary planes this module is present on (deployment,
   *  network, …), sorted. Omitted when the module lives on the primary plane
   *  only. Drives plane boards (presence-subset filtering + the plane switcher). */
  readonly planes?: readonly string[];
  /** True for a stub synthesized from a process reference (v0.6, §4.13) — the
   *  renderer draws it dashed. Omitted (undefined) for authored modules. */
  readonly synthesized?: boolean;
  /** True when the module's name was derived from its type (§4.14, an unnamed
   *  module). Omitted for authored-name modules. */
  readonly unnamed?: boolean;
}

export interface WireTypeFieldEntry {
  readonly path: readonly string[];
  readonly value?: unknown;
  readonly required: boolean;
  readonly cascade: boolean;
  readonly append: boolean;
}

export interface WireTypeAspectEntry {
  readonly path: readonly string[];
  readonly value?: unknown;
  readonly required: boolean;
  readonly drop: boolean;
}

export interface WireTypeDef {
  readonly id?: string;
  readonly name: string;
  readonly parentType: string;
  readonly exported: boolean;
  readonly description?: string;
  readonly fields: readonly WireTypeFieldEntry[];
  readonly aspects: readonly WireTypeAspectEntry[];
  /** Dotted package name this type originates from (e.g. `arch.extras`). */
  readonly library?: string;
  /** Dotted name of the declaring space (v0.5); undefined at package root. */
  readonly space?: string;
}

export interface WireProcess {
  readonly id?: string;
  readonly name: string;
  readonly description?: string;
  /**
   * The VERBATIM AST step list (`ProcessStep[]`), not the resolved IR or a
   * walk. So `from` keeps its AST meaning — the caller **as written**, never
   * resolved: `[]` for an omitted head (`> Callee`) and for both line glyphs,
   * with `fromDitto` / `fromSnake` naming which glyph it was. A consumer that
   * wants the RESOLVED caller must build the IR (`CallHop.caller`); back-filling
   * `from` would make it disagree with the source text. Pinned by
   * `serialize.test.ts` "process steps carry the as-written caller + glyph flags".
   */
  readonly steps: unknown;
  readonly subprocesses?: unknown;
  /** v0.9.0 §2.7: header-zone fields (`team: "Commerce"`), verbatim AST. */
  readonly fields?: unknown;
  /** v0.10 §5.1: RESOLVED classification aspects (owner-module cascade +
   *  own override), so `@@team` reads a module-scoped process the same as a
   *  module. Absent when the process carries none. */
  readonly aspects?: readonly WireAspectEntry[];
  /** Qualified path of the owning module for module-scoped processes. */
  readonly ownerPath?: readonly string[];
  /** POSIX path of the `.arch` file this process was declared in. */
  readonly sourceFile?: string;
  /** Dotted name of the enclosing space (v0.5); undefined at package root. */
  readonly space?: string;
}

export interface WireSubprocess {
  readonly id?: string;
  readonly name: string;
  readonly params: readonly string[];
  readonly description?: string;
  readonly steps: unknown;
  readonly subprocesses?: unknown;
  /** Owner scope: module qualified path or owning process qualified name. */
  readonly ownerPath?: readonly string[];
  /** v0.11: dotted LANDED identity space; undefined at package root. */
  readonly space?: string;
}

/**
 * Metadata-display wire shape (nav list, diff panel). The view-v2 clauses are
 * NOT serialized: the board reads them off the in-browser engine `model.views`
 * (the viewer re-resolves client-side), so the wire carries only identity +
 * prose. A future server-only render path would add a serialized-clause field.
 */
export interface WireView {
  readonly id?: string;
  readonly name: string;
  readonly description?: string;
  /** v0.11: dotted LANDED identity space; undefined at package root. */
  readonly space?: string;
}

export interface WireArchitectureModel {
  readonly modules: readonly WireModule[];
  readonly processes: readonly WireProcess[];
  readonly subprocesses: readonly WireSubprocess[];
  readonly views: readonly WireView[];
  readonly types: readonly WireTypeDef[];
  /**
   * Distinct space names present in the model (v0.5), sorted. Empty/undefined
   * for single-space packages. Lets the viewer enumerate spaces without
   * scanning every node's `space` field.
   */
  readonly spaces?: readonly string[];
  /** Markdown documents (§4.15) — rendered prose + section anchors. */
  readonly documents?: readonly WireMarkdownDocument[];
  /** "Mentioned in" backlinks keyed by an element's qualified name, `#id`, or
   *  a document name. Empty/absent when nothing references anything. */
  readonly backlinks?: Readonly<Record<string, readonly WireBacklink[]>>;
}

export interface WireMarkdownDocument {
  readonly name: string;
  readonly path: string;
  readonly space?: string;
  /** The body rendered through the flavored-markdown grammar (archlang:// links
   *  for refs); the viewer renders this markdown to HTML. */
  readonly markdown: string;
  readonly sections: readonly { readonly text: string; readonly slug: string; readonly level: number }[];
}

export interface WireBacklink {
  readonly kind: "element" | "document";
  readonly name: string;
  readonly qualifiedName?: string;
  readonly path?: string;
}

/**
 * The wire form of a derived edge. The v0.10 edge universe is discriminated by
 * `kind`: `call` fills the module→interface fields, `do` fills the process→
 * process fields. Kept a single flat bag (rather than a closed union) so the
 * wire stays FORWARD-TOLERANT — a consumer that only handles `call` edges skips
 * anything else via {@link isCallWireEdge}, and an unknown future `kind` parses
 * without a schema bump. A legacy payload with no `kind` reads as `call`.
 */
export interface WireDependencyEdge {
  /** `"call"` | `"do"` | a future kind. Absent ⇒ legacy `call`. */
  readonly kind?: string;
  // call-edge fields (present when kind === "call")
  readonly fromModulePath?: readonly string[];
  readonly fromModuleId?: string;
  readonly toModulePath?: readonly string[];
  readonly toModuleId?: string;
  readonly toInterfacePath?: readonly string[];
  readonly compensation?: boolean;
  /** v0.11 (D2a): each endpoint's dotted LANDED space — keeps the scene
   *  edge↔node join exact when two spaces root same-named modules. */
  readonly fromSpace?: string;
  readonly toSpace?: string;
  // do-edge fields (present when kind === "do")
  readonly fromProcessId?: string;
  readonly fromProcessName?: string;
  readonly toProcessId?: string;
  readonly toProcessName?: string;
  readonly targetIsSubprocess?: boolean;
  // common
  readonly source: "process";
  readonly processId?: string;
  readonly processName?: string;
  readonly stepIndex?: number;
  readonly altCondition?: string;
}

/** True for the historical module→interface `call` edge (a missing `kind` on a
 *  legacy payload reads as `call`). Board render / layout / module diff work
 *  over these only — they skip `do` and any unknown future kind. */
export function isCallWireEdge(e: WireDependencyEdge): boolean {
  return (e.kind ?? "call") === "call";
}

/** Tolerant filter to the `call` edges of a wire edge stream — the choke point
 *  where a board consumer keeps rendering as the universe grows (design P0.1). */
export function callWireEdges(
  edges: readonly WireDependencyEdge[]
): readonly WireDependencyEdge[] {
  return edges.filter(isCallWireEdge);
}

export interface WireDiagnostic {
  readonly severity: "error" | "warning" | "todo" | "info" | "hint";
  /** English fallback rendering. */
  readonly message: string;
  readonly code?: string;
  /** Catalog key + params so the viewer re-renders the message in its own UI
   *  locale via the engine's `diagnosticText()`. */
  readonly msgKey?: string;
  readonly params?: Readonly<Record<string, unknown>>;
  readonly source?: unknown;
}

type DescIndex = ReturnType<typeof buildDescriptionIndex>;
type SpaceOf = (file: string | undefined) => string | undefined;
type ModuleSerializer = (
  m: ResolvedModule,
  descIndex: DescIndex,
  spaceOf: SpaceOf,
  presence?: ResolvedModel["presence"]
) => WireModule;

/** Serialize a model given a description index + a per-module serializer (so the
 *  incremental path can substitute a cached one). The non-module sections are
 *  small relative to the module count. */
interface DocAux {
  readonly documents: readonly WireMarkdownDocument[] | undefined;
  readonly backlinks: Readonly<Record<string, readonly WireBacklink[]>> | undefined;
}

/** Render the documents + build the backlink index — the O(all-descriptions)
 *  parts. Cached on structural edits by the incremental serializer so a value
 *  edit doesn't pay for it. */
export function serializeDocAux(model: ResolvedModel, descIndex: DescIndex): DocAux {
  const docs = model.documents ?? [];
  const documents =
    docs.length > 0
      ? docs.map((d) => ({
          name: d.name,
          path: d.path,
          space: d.space && d.space.length > 0 ? d.space.join(".") : undefined,
          markdown: renderMarkdownDocument(
            d.body,
            makeDescriptionContext(descIndex, () => undefined, d.space)
          ).markdown,
          sections: d.sections.map((s) => ({ text: s.text, slug: s.slug, level: s.level })),
        }))
      : undefined;
  return { documents, backlinks: serializeBacklinks(model, docs) };
}

// Unicode-aware like GETTER_RE (engine markdown/refs.ts) — an ASCII-only head
// left unicode foreign getters' rendered values stale after value edits (r46).
const FOREIGN_GETTER_RE = /\]\]@@?[\p{ID_Start}_]/u;

/** True if any description or document body uses a foreign getter
 *  `[[X]]@field` / `[[X]]@@aspect` — the only construct whose rendering depends
 *  on another node's field/aspect VALUE (which can change without a structural
 *  delta). */
function modelHasForeignGetters(model: ResolvedModel): boolean {
  let found = false;
  const scan = (text: string): void => {
    if (!found && FOREIGN_GETTER_RE.test(text)) found = true;
  };
  const scanDescs = (descs: readonly { text: string }[]): void => {
    for (const d of descs) scan(d.text);
  };
  const visitI = (i: ResolvedInterface): void => scanDescs(i.body.descriptions);
  const visitS = (s: ResolvedSurface): void => {
    scanDescs(s.body.descriptions);
    s.interfaces.forEach(visitI);
    s.surfaces.forEach(visitS);
  };
  const visitM = (m: ResolvedModule): void => {
    scanDescs(m.body.descriptions);
    m.interfaces.forEach(visitI);
    m.surfaces.forEach(visitS);
    m.modules.forEach(visitM);
  };
  model.modules.forEach(visitM);
  for (const t of model.types) if (!found) scanDescs(t.body.descriptions);
  for (const p of model.processes) if (!found && p.description) scan(p.description);
  for (const d of model.documents ?? []) if (!found) scan(d.body);
  return found;
}

function serializeShell(
  model: ResolvedModel,
  descIndex: DescIndex,
  serializeMod: ModuleSerializer,
  aux?: DocAux
): WireArchitectureModel {
  const spaceOf: SpaceOf = (file) => {
    if (file === undefined) return undefined;
    const s = model.spaces?.get(file);
    return s && s.length > 0 ? s.join(".") : undefined;
  };
  const distinctSpaces = new Set<string>();
  if (model.spaces) {
    for (const s of model.spaces.values()) {
      if (s.length > 0) distinctSpaces.add(s.join("."));
    }
  }
  const { documents, backlinks } = aux ?? serializeDocAux(model, descIndex);
  // Pair each process DECL to its resolved IR (which carries the cascaded
  // aspects) by source position — the stable key the graph uses too.
  const procModelBySource = new Map<string, { aspects?: readonly AspectEntry[] }>();
  for (const pm of model.processModels ?? []) {
    const s = pm.source;
    if (s) procModelBySource.set(`${s.file}:${s.start.offset}`, pm);
  }
  return {
    documents,
    backlinks,
    modules: model.modules.map((m) => serializeMod(m, descIndex, spaceOf, model.presence)),
    processes: model.processes.map((p) => ({
      // `processIdentity` gives an anonymous process (no `#id`) a stable
      // content-keyed id so several anon processes don't collapse to one entry
      // in the model index (and their edges carry the matching processId).
      id: processIdentity(p),
      name: p.name,
      description: p.description
        ? renderProcessDescription(p.description, descIndex)
        : undefined,
      steps: p.steps,
      subprocesses: p.subprocesses,
      fields: p.fields,
      ...(() => {
        const pm = p.source ? procModelBySource.get(`${p.source.file}:${p.source.start.offset}`) : undefined;
        return pm?.aspects && pm.aspects.length > 0 ? { aspects: pm.aspects.map(serializeAspect) } : {};
      })(),
      ownerPath: p.ownerPath,
      sourceFile: p.source?.file,
      // v0.11 (D1a): the LANDED identity space (resolver-stamped — a placed
      // process carries its target's), falling back to the declaring file's.
      space: p.space && p.space.length > 0 ? p.space.join(".") : spaceOf(p.source?.file),
    })),
    subprocesses: [
      ...model.subprocesses.map((sp) => ({
        id: sp.id,
        name: sp.name,
        params: sp.params,
        description: sp.description
          ? renderProcessDescription(sp.description, descIndex)
          : undefined,
        steps: sp.steps,
        subprocesses: sp.subprocesses,
        ownerPath: sp.ownerPath,
        space: sp.space && sp.space.length > 0 ? sp.space.join(".") : spaceOf(sp.source?.file),
      })),
      // Process-NESTED subprocess decls live only on their process in the
      // resolved model — hoist them onto the wire with ownerPath = the
      // qualified process name (the scene contract), or ⌘F and every other
      // model.subprocesses consumer never sees them (r51). Anonymous
      // processes are skipped: their nested subs have no addressable owner.
      ...model.processes.flatMap((p) =>
        p.name
          ? (p.subprocesses ?? []).map((sp) => ({
              id: sp.id,
              name: sp.name,
              params: sp.params,
              description: sp.description
                ? renderProcessDescription(sp.description, descIndex)
                : undefined,
              steps: sp.steps,
              subprocesses: sp.subprocesses,
              ownerPath: [...(p.ownerPath ?? []), p.name],
              // The host process's landed space (rev-1 F6: two spaces'
              // same-named processes must not conflate their private subs).
              space:
                p.space && p.space.length > 0 ? p.space.join(".") : spaceOf(p.source?.file),
            }))
          : [],
      ),
    ],
    views: model.views.map((v) => ({
      id: v.id,
      name: v.name,
      description: v.description
        ? renderProcessDescription(v.description, descIndex)
        : undefined,
      space: v.space && v.space.length > 0 ? v.space.join(".") : spaceOf(v.source?.file),
    })),
    types: model.types.map((t) => ({
      id: t.id,
      name: t.name,
      parentType: t.parentType,
      exported: t.exported ?? false,
      description: renderBodyDescription(t.body.descriptions, t.body.aspects, t.body.fields, descIndex, t.decorations),
      fields: t.body.fields.map(serializeTypeField),
      aspects: t.body.aspects.map(serializeTypeAspect),
      library: t.library,
      space: spaceOf(t.source?.file),
    })),
    spaces: distinctSpaces.size > 0 ? [...distinctSpaces].sort() : undefined,
  };
}

export function serializeModel(model: ResolvedModel): WireArchitectureModel {
  return serializeShell(model, buildDescriptionIndex(model, model.documents ?? []), serializeModule);
}

/**
 * Build the "mentioned in" backlink record (§4.10 / §4.15), keyed so the viewer
 * can look up by a node's short name, qualified name, `#id`, or a document
 * name. Structural — built once per serialize, not on the hot value-edit path.
 */
function serializeBacklinks(
  model: ResolvedModel,
  documents: readonly { name: string }[]
): Readonly<Record<string, readonly WireBacklink[]>> | undefined {
  const idx = buildBacklinkIndex(model, documents as never);
  const out: Record<string, readonly WireBacklink[]> = {};
  const toWire = (b: Backlink): WireBacklink => ({
    kind: b.kind,
    name: b.name,
    qualifiedName: b.qualifiedName,
    path: b.path,
  });
  const add = (key: string, links: readonly Backlink[]): void => {
    if (links.length > 0 && out[key] === undefined) out[key] = links.map(toWire);
  };
  const visitInterface = (i: ResolvedInterface, q: string): void => {
    add(i.name, idx.forName(i.name));
    add(q, idx.forName(q));
  };
  const visitSurface = (s: ResolvedSurface, parentQ: string, moduleQ: string): void => {
    const q = `${parentQ}.${s.name}`;
    add(s.name, idx.forName(s.name));
    add(q, idx.forName(q));
    for (const i of s.interfaces) {
      visitInterface(i, `${q}.${i.name}`);
      // Surface-ELIDED alias: the app addresses surface-nested interfaces by
      // `${moduleQual}.${name}` (flattenInterfaces elides the surface
      // segment), so the Mentioned-in panel looks the mention up under the
      // elided key (r52). First-write-wins in `add` keeps a same-named
      // module-direct interface's own record intact.
      add(`${moduleQ}.${i.name}`, idx.forName(`${q}.${i.name}`));
    }
    for (const sub of s.surfaces) visitSurface(sub, q, moduleQ);
  };
  const visitModule = (m: ResolvedModule, parent: string): void => {
    const q = parent ? `${parent}.${m.name}` : m.name;
    add(m.name, idx.forName(m.name));
    add(q, idx.forName(q));
    if (m.id) add(`#${m.id}`, idx.forId(m.id));
    for (const i of m.interfaces) visitInterface(i, `${q}.${i.name}`);
    for (const s of m.surfaces) visitSurface(s, q, q);
    for (const c of m.modules) visitModule(c, q);
  };
  for (const m of model.modules) visitModule(m, "");
  for (const d of documents) add(d.name, idx.forDocument(d.name));
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Stateful incremental serializer for the browser edit loop. Caches each
 * top-level module's wire form by resolved-module identity (the compiler
 * preserves identity for unchanged subtrees) and the wire edges by the graph's
 * edge-array identity (the graph reuses the array across value edits). A
 * `structural` edit (modules/decls added/removed/renamed → the description
 * index's targets change) rebuilds the index and re-serializes everything;
 * a value edit re-serializes only the changed module.
 */
export function createIncrementalSerializer(): {
  serializeModel(model: ResolvedModel, structural: boolean): WireArchitectureModel;
  serializeEdges(graph: DependencyGraph): readonly WireDependencyEdge[];
} {
  const moduleCache = new WeakMap<ResolvedModule, { v: number; wire: WireModule }>();
  let descIndex: DescIndex | null = null;
  let docAux: DocAux | null = null;
  let version = 0;
  const edgeCache = new WeakMap<readonly DependencyEdge[], readonly WireDependencyEdge[]>();
  // A foreign getter `[[X]]@field` renders X's LABEL VALUE, which a value edit
  // changes WITHOUT a structural delta. When any exist we can't trust the
  // structural-only cache for the description/document index, so rebuild it
  // every serialize (and bump the per-module + doc caches). Detected once per
  // structural build — the common case (no foreign getters) keeps the cache.
  let hasForeignGetters = false;

  return {
    serializeModel(model, structural) {
      // Recompute every serialize, not just on structural builds: a foreign
      // getter `[[X]]@field` can be INTRODUCED by a non-structural desc/value
      // edit. If it were only sampled on structural builds, a getter added
      // after the first build would leave this `false`, and the branch below
      // (which rebuilds the index when getters exist) would never fire — so a
      // later change to the referenced node's label would render stale.
      hasForeignGetters = modelHasForeignGetters(model);
      if (structural || descIndex === null) {
        descIndex = buildDescriptionIndex(model, model.documents ?? []);
        docAux = serializeDocAux(model, descIndex);
        version++;
      } else if (hasForeignGetters) {
        descIndex = buildDescriptionIndex(model, model.documents ?? []);
        docAux = serializeDocAux(model, descIndex);
        version++; // invalidate the per-module wire cache (foreign values moved)
      }
      const idx = descIndex;
      const v = version;
      const serializeMod: ModuleSerializer = (m, di, spaceOf, presence) => {
        const cached = moduleCache.get(m);
        if (cached && cached.v === v) return cached.wire;
        const wire = serializeModule(m, di, spaceOf, presence);
        moduleCache.set(m, { v, wire });
        return wire;
      };
      return serializeShell(model, idx, serializeMod, docAux ?? undefined);
    },
    serializeEdges(graph) {
      const cached = edgeCache.get(graph.edges);
      if (cached) return cached;
      const wire = serializeEdges(graph);
      edgeCache.set(graph.edges, wire);
      return wire;
    },
  };
}

/**
 * Render a node's descriptions to interpolated markdown — refs
 * resolved to `[name](archlang://…)` links and `@label` substituted
 * with values from the owning node's labels (Spec §4.11). The web
 * client only needs to handle markdown → HTML; archlang-specific
 * syntax is fully resolved here.
 */
function renderBodyDescription(
  entries: readonly { text: string }[],
  aspects: readonly AspectEntry[],
  fields: readonly FieldDecl[],
  index: ReturnType<typeof buildDescriptionIndex>,
  decorations?: readonly DecorationInstance[]
): string | undefined {
  const joined = joinDescriptions(entries as never);
  if (!joined) return undefined;
  // One constructor for a node-owned description, so both axes are composed
  // together and neither can be half-built here.
  const ctx = nodeDescriptionContext(index, { aspects, fields, decorations });
  return renderDescription(joined, ctx).markdown;
}

/**
 * Same, for declarations whose description is stored as a flat string
 * (processes, subprocesses, views) with no labels of their own. Refs
 * still resolve; `@label` interpolation degrades to `<missing:…>`.
 */
function renderProcessDescription(
  description: string,
  index: ReturnType<typeof buildDescriptionIndex>
): string {
  const ctx: DescriptionContext = makeDescriptionContext(index, () => undefined);
  return renderDescription(description, ctx).markdown;
}

export function serializeEdges(graph: DependencyGraph): readonly WireDependencyEdge[] {
  return graph.edges.map((e): WireDependencyEdge => {
    if (e.kind === "do") {
      return {
        kind: "do",
        fromProcessId: e.fromProcessId,
        fromProcessName: e.fromProcessName,
        toProcessId: e.toProcessId,
        toProcessName: e.toProcessName,
        targetIsSubprocess: e.targetIsSubprocess,
        source: e.source,
        processId: e.processId,
        processName: e.processName,
        stepIndex: e.stepIndex,
        altCondition: e.altCondition,
      };
    }
    return {
      kind: "call",
      fromModulePath: e.fromModulePath,
      fromModuleId: e.fromModuleId,
      toModulePath: e.toModulePath,
      toModuleId: e.toModuleId,
      toInterfacePath: e.toInterfacePath,
      compensation: e.compensation,
      ...(e.fromSpace && e.fromSpace.length > 0 ? { fromSpace: e.fromSpace.join(".") } : {}),
      ...(e.toSpace && e.toSpace.length > 0 ? { toSpace: e.toSpace.join(".") } : {}),
      source: e.source,
      processId: e.processId,
      processName: e.processName,
      stepIndex: e.stepIndex,
      altCondition: e.altCondition,
    };
  });
}

export function serializeDiagnostics(diags: readonly Diagnostic[]): readonly WireDiagnostic[] {
  return diags.map((d) => ({
    severity: d.severity,
    message: d.message,
    code: d.code,
    msgKey: d.msgKey,
    params: d.params,
    source: d.source,
  }));
}

function serializeModule(
  m: ResolvedModule,
  index: ReturnType<typeof buildDescriptionIndex>,
  spaceOf: (file: string | undefined) => string | undefined,
  presence?: ResolvedModel["presence"],
  parentPath: readonly Name[] = []
): WireModule {
  const path = [...parentPath, m.name];
  // §1.1: non-primary planes this module is present on (primary is the implicit
  // structural plane and never a filter target).
  const planeSet = presence?.planesOf.get(path.join("."));
  const planes = planeSet ? [...planeSet].filter((p) => p !== "primary").sort() : [];
  // §1.2: partial presence makes a member's presence differ from its node's — one
  // DECLARED in a block is absent from primary, one merely REFERENCED there rides
  // both, and a main-body member of the same node does not ride at all. Presence
  // records a per-member set for exactly those nodes, so an entry is the signal
  // that this node's surface is plane-dependent; then EVERY member of it is
  // tagged, or the untagged ones would draw on every plane. A model with no
  // partial presence has no member entries, so every member stays untagged and
  // the wire is byte-identical to before.
  const exclusiveTo = (memberPath: readonly Name[]): WirePlanePresent => {
    const set = presence?.planesOf.get(memberPath.join("."));
    return set ? { planes: [...set].sort() } : {};
  };
  return {
    // Explicit `#id`, else a qualified-name fallback — keeps id-less modules
    // selectable/diffable in the UI (matches core's diff id).
    id: moduleDiffId(m, parentPath),
    name: m.name,
    typeName: m.typeName,
    kindName: m.typeName,
    baseType: m.baseType,
    description: renderBodyDescription(m.body.descriptions, m.body.aspects, m.body.fields, index, m.decl.decorations),
    fields: m.body.fields.map((f) => ({ ...serializeField(f), ...exclusiveTo([...path, ...f.path]) })),
    aspects: m.body.aspects.map(serializeAspect),
    ...(m.decl.decorations?.length
      ? { decorations: m.decl.decorations.map(serializeDecoration) }
      : {}),
    interfaces: m.interfaces.map((i) => ({ ...serializeInterface(i, index), ...exclusiveTo([...path, i.name]) })),
    surfaces: m.surfaces.map((f) => ({ ...serializeSurface(f, index), ...exclusiveTo([...path, f.name]) })),
    children: m.modules.map((c) =>
      serializeModule(c, index, spaceOf, presence, [...parentPath, m.name])
    ),
    sourceFile: m.decl.source?.file,
    // v0.11 (D1a): LANDED identity space (resolver-stamped — a placed subtree
    // carries its target's), falling back to the declaring file's.
    space: m.space && m.space.length > 0 ? m.space.join(".") : spaceOf(m.decl.source?.file),
    ...(planes.length ? { planes } : {}),
    ...(m.synthesized ? { synthesized: true } : {}),
    ...(m.unnamed ? { unnamed: true } : {}),
  };
}

/** Flatten a decoration's args to text — the inspector renders them, and the
 *  wire format stays free of engine Value unions. */
/**
 * A decoration argument as a reader sees it.
 *
 * NOT `valueText`, which reproduces source syntax: a `reason:` would come back
 * with its quotes still on, and an inspector would print `"SMTP retired"`
 * inside its own quotation. Shared with `entity-details.ts` so the wire form
 * and the inspector cannot disagree about what an argument says.
 */
export function decorationArgText(value: Value): string {
  return value.kind === "string" || value.kind === "identifier"
    ? value.text
    : value.kind === "ref"
      ? value.path.join(".")
      : value.kind === "number" || value.kind === "boolean"
        ? String(value.value)
        : "";
}

function serializeDecoration(d: DecorationInstance): {
  name: string;
  args: { name?: string; value: string }[];
} {
  return {
    name: d.name,
    args: d.args.map((a) => ({
      ...(a.name !== undefined ? { name: a.name } : {}),
      value: decorationArgText(a.value),
    })),
  };
}

function serializeSurface(
  f: ResolvedSurface,
  index: ReturnType<typeof buildDescriptionIndex>
): WireSurface {
  return {
    name: f.name,
    typeName: f.typeName,
    kindName: f.typeName,
    description: renderBodyDescription(f.body.descriptions, f.body.aspects, f.body.fields, index, f.decl.decorations),
    fields: f.body.fields.map(serializeField),
    aspects: f.body.aspects.map(serializeAspect),
    interfaces: f.interfaces.map((i) => serializeInterface(i, index)),
    surfaces: f.surfaces.map((sub) => serializeSurface(sub, index)),
  };
}

function serializeInterface(
  i: ResolvedInterface,
  index: ReturnType<typeof buildDescriptionIndex>
): WireInterface {
  return {
    name: i.name,
    typeName: i.typeName,
    kindName: i.typeName,
    description: renderBodyDescription(i.body.descriptions, i.body.aspects, i.body.fields, index, i.decl.decorations),
    fields: i.body.fields.map(serializeField),
    aspects: i.body.aspects.map(serializeAspect),
    ...(i.synthesized ? { synthesized: true } : {}),
    ...(i.anonymous ? { anonymous: true } : {}),
  };
}

function serializeField(f: {
  path: readonly string[];
  value?: unknown;
  from?: { kind: string; path?: readonly string[] };
  fromTemplate?: boolean;
}): WireFieldEntry {
  // Getter provenance travels with the value: the engine publishes a chip's
  // key (`label1Key`) FROM it at widget-spec extraction, and every renderer
  // extracts from this wire shape — dropping `from` here meant the key existed
  // only where nothing renders.
  const from =
    f.from?.kind === "getter" && f.from.path?.length
      ? { from: { kind: "getter" as const, path: f.from.path } }
      : {};
  return { path: f.path, value: f.value, ...from, ...(f.fromTemplate ? { fromTemplate: true } : {}) };
}

function serializeAspect(l: { path: readonly string[]; value?: unknown }): WireAspectEntry {
  return { path: l.path, value: l.value };
}

function serializeTypeField(f: {
  path: readonly string[];
  value?: unknown;
  required?: boolean;
  cascade?: boolean;
  append?: boolean;
}): WireTypeFieldEntry {
  return {
    path: f.path,
    value: f.value,
    required: f.required ?? false,
    cascade: f.cascade ?? false,
    append: f.append ?? false,
  };
}

function serializeTypeAspect(l: {
  path: readonly string[];
  value?: unknown;
  required?: boolean;
  drop?: boolean;
}): WireTypeAspectEntry {
  return {
    path: l.path,
    value: l.value,
    required: l.required ?? false,
    drop: l.drop ?? false,
  };
}
